/* eslint-disable max-lines -- HB-1924 added 4xx backoff branch; existing polling lifecycle + error handling is already at the cap */
import { useCallback, useEffect, useRef } from "react";

import { notifyError } from "common/utils/app-monitoring/notify";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import { track } from "jiffy-analytics";
import { api, type models } from "jiffy-api";
import { shouldReportError } from "common/utils/request/request";
import { nextPollDelay } from "common/utils/polling-backoff";
import {
  INITIAL_DELAY,
  POLLING_INTERVAL,
  MAX_POLLING_DURATION,
  normalizeArtworkResponse
} from "@design-lab/shared/lib/enhancementPolling";
import type {
  PollingState,
  NormalizedArtwork,
  RawArtworkResponse
} from "@design-lab/shared/lib/enhancementPolling";

type UseEnhancementPollingOptions = {
  artworkId: number;
  startTime: number;
  onEnhancementComplete: (artwork: NormalizedArtwork) => void | Promise<void>;
  onEnhancementFailed?: (errorMessage: string) => void;
  enabled?: boolean;
  isArtworkPresent?: boolean;
};

type GetArtworkHistoryWithOptions = (
  params: models.dtf.getArtworkHistory.Request,
  options?: { silent?: boolean }
) => Promise<models.dtf.getArtworkHistoryDetails.Response>;

type PollingStateWithAttempt = PollingState & {
  attempt: number;
  lastStatus?: number | undefined;
  nextDelayMs?: number | undefined;
};

export function useEnhancementPolling({
  artworkId,
  startTime,
  onEnhancementComplete,
  onEnhancementFailed,
  enabled = true,
  isArtworkPresent = true
}: UseEnhancementPollingOptions) {
  const pollingState = useRef<PollingStateWithAttempt>({
    isPolling: false,
    pollCount: 0,
    attempt: 0
  });

  const stopPolling = useCallback(() => {
    if (pollingState.current.timeoutId) {
      clearTimeout(pollingState.current.timeoutId);
      pollingState.current.timeoutId = undefined;
    }
    pollingState.current.isPolling = false;
    pollingState.current.attempt = 0;
    pollingState.current.lastStatus = undefined;
    pollingState.current.nextDelayMs = undefined;
  }, []);

  const pollArtworkDetails = useCallback(async () => {
    if (!enabled || !pollingState.current.isPolling) {
      return;
    }

    if (pollingState.current.pollCount === 1 && artworkId) {
      track("fallback_polling_started", {
        artwork_id: String(artworkId),
        page: "design_lab"
      });
    }

    try {
      const getArtworkHistory = api.dtf.getArtworkHistory as GetArtworkHistoryWithOptions;
      const rawResponse = await getArtworkHistory(
        {
          artwork_ids: [artworkId],
          catalog_number: "DTF001"
        } as models.dtf.getArtworkHistory.Request,
        { silent: true }
      );

      const response = objNamesToCamelCase(
        rawResponse
      ) as unknown as models.dtf.getArtworkHistoryDetails.Response;
      pollingState.current.attempt = 0;
      pollingState.current.lastStatus = undefined;
      pollingState.current.nextDelayMs = undefined;

      const artwork = response.artworks.find(
        (a: models.dtf.getArtworkHistoryDetails.DetailedArtwork) => Number(a.id) === artworkId
      );

      const normalizedArtwork = artwork
        ? normalizeArtworkResponse(artwork as RawArtworkResponse)
        : null;
      const enhancementState = normalizedArtwork?.currentEnhancementState;

      if (enhancementState === "complete_processing") {
        stopPolling();

        track("fallback_polling_enhancement_completed", {
          artwork_id: String(artworkId),
          page: "design_lab"
        });

        if (normalizedArtwork) {
          onEnhancementComplete(normalizedArtwork);
        }
        return;
      }
    } catch (error) {
      const err = error as { status?: number; response?: unknown; message?: string; name?: string };
      const status = err?.status;
      if (status === 401) {
        stopPolling();
        if (artworkId) {
          track("fallback_polling_stopped_session_expired", {
            artwork_id: String(artworkId),
            page: "design_lab"
          });
        }
        return;
      }
      if (shouldReportError(error) && !(status && status >= 400 && status < 500)) {
        notifyError("Enhancement polling error", {
          params: {
            error: error instanceof Error ? error.message : String(error),
            error_object: error instanceof Error ? error.toString() : String(error),
            stack_trace: error instanceof Error ? error.stack : null,
            hook: "useEnhancementPolling",
            artworkId
          }
        });
      }

      const elapsedTime = Date.now() - startTime;
      const result = nextPollDelay({
        error: err,
        attempt: pollingState.current.attempt,
        lastStatus: pollingState.current.lastStatus,
        baseIntervalMs: POLLING_INTERVAL,
        capMs: 45000,
        remainingBudgetMs: MAX_POLLING_DURATION - elapsedTime
      });

      if (result.shouldStop) {
        stopPolling();
        return;
      }

      pollingState.current.attempt = result.attemptNext;
      pollingState.current.lastStatus = result.lastStatusNext;
      pollingState.current.nextDelayMs = result.delayMs;
    }

    // Schedule next poll after processing
    if (!pollingState.current.isPolling) {
      return;
    }

    const elapsedTime = Date.now() - startTime;

    if (elapsedTime >= MAX_POLLING_DURATION) {
      stopPolling();

      if (artworkId) {
        const artworkDetails = await api.dtf.getArtwork(
          { id: artworkId },
          { catalog_number: "DTF001" }
        );
        if (artworkDetails.enhancement_status === "failed") {
          onEnhancementFailed?.("Something went wrong. Please try again.");
        }

        track("fallback_polling_stopped", {
          artwork_id: String(artworkId),
          page: "design_lab"
        });
      }
      return;
    }

    const nextInterval = pollingState.current.nextDelayMs ?? POLLING_INTERVAL;
    const remainingTime = MAX_POLLING_DURATION - elapsedTime;

    if (nextInterval > remainingTime) {
      return;
    }

    pollingState.current.timeoutId = setTimeout(() => {
      pollingState.current.pollCount += 1;
      pollArtworkDetails();
    }, nextInterval);
  }, [artworkId, startTime, onEnhancementComplete, onEnhancementFailed, enabled, stopPolling]);

  const startPolling = useCallback(() => {
    if (pollingState.current.isPolling || !enabled) {
      return;
    }

    pollingState.current.isPolling = true;
    pollingState.current.pollCount = 0;
    pollingState.current.attempt = 0;
    pollingState.current.nextDelayMs = undefined;

    pollingState.current.timeoutId = setTimeout(() => {
      pollingState.current.pollCount += 1;
      pollArtworkDetails();
    }, INITIAL_DELAY);
  }, [enabled, pollArtworkDetails]);

  useEffect(() => {
    if (!isArtworkPresent && pollingState.current.isPolling) {
      stopPolling();

      if (artworkId) {
        track("fallback_polling_artwork_removed", {
          artwork_id: String(artworkId),
          page: "design_lab"
        });
      }
    }
  }, [isArtworkPresent, stopPolling, artworkId]);

  useEffect(
    () => () => {
      stopPolling();
    },
    [stopPolling]
  );

  return {
    startPolling,
    stopPolling,
    isPolling: pollingState.current.isPolling
  };
}
